// src/pages/AdvancedReportsPage.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Button, Chip, FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, LinearProgress, Alert
} from '@mui/material';
import {
  PictureAsPdf, Analytics, TrendingUp, People, Schedule,
  Assessment, Download, FilterList, CalendarToday,
  AutoGraph, Insights, BarChart as BarChartIcon, PieChart as PieChartIcon, Timeline
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, // 👈 Виправлено імпорт
  AreaChart, Area, ComposedChart, Scatter
} from 'recharts';

import { getData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';
import { useNotifications } from '../context/NotificationContext'; // 👈 Додано імпорт
import pdfMake from "pdfmake/build/pdfmake";
import { vfs } from "pdfmake/build/vfs_fonts";
pdfMake.vfs = vfs;

const AdvancedReportsPage = () => {
  const { t } = useI18n();
  const { showNotification } = useNotifications(); // 👈 Додано деструктуризацію
  
  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Фільтри
  const [reportType, setReportType] = useState('comprehensive');
  const [timeRange, setTimeRange] = useState('month');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const staffData = await getData('staff') || [];
      const attendanceData = await getData('attendance_records') || [];
      const shiftsData = await getData('shift_records') || [];
      
      setStaff(staffData.filter(s => s.status === 'active'));
      setAttendance(attendanceData);
      setShifts(shiftsData);
    } catch (error) {
      console.error("Error loading data:", error);
      showNotification(t('reports.load_error'), 'error'); // 👈 Тепер showNotification доступний
    } finally {
      setLoading(false);
    }
  };

  // Фільтрація даних
  const filteredData = useMemo(() => {
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    
    const filteredAttendance = attendance.filter(record => {
      const recordDate = new Date(record.date);
      return recordDate >= startDate && recordDate <= endDate;
    });

    const filteredShifts = shifts.filter(shift => {
      const shiftDate = new Date(shift.date);
      return shiftDate >= startDate && shiftDate <= endDate;
    });

    return { filteredAttendance, filteredShifts };
  }, [attendance, shifts, dateRange]);

  // Генерація звітів
  const reportData = useMemo(() => {
    return generateReportData(reportType, staff, filteredData.filteredAttendance, filteredData.filteredShifts);
  }, [reportType, staff, filteredData]);

  const reportTypes = [
    { value: 'comprehensive', label: t('reports.comprehensive'), icon: <Analytics />, color: 'primary' },
    { value: 'attendance', label: t('reports.attendance'), icon: <People />, color: 'success' },
    { value: 'performance', label: t('reports.performance'), icon: <TrendingUp />, color: 'info' },
    { value: 'shifts', label: t('reports.shifts'), icon: <Schedule />, color: 'warning' },
    { value: 'department', label: t('reports.department'), icon: <Assessment />, color: 'secondary' }
  ];

  const timeRanges = [
    { value: 'week', label: t('reports.week') },
    { value: 'month', label: t('reports.month') },
    { value: 'quarter', label: t('reports.quarter') },
    { value: 'custom', label: t('reports.custom') }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Заголовок */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('reports.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('reports.subtitle')}
        </Typography>
      </Box>

      {/* Панель керування */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Тип звіту */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.report_type')}</InputLabel>
                <Select
                  value={reportType}
                  label={t('reports.report_type')}
                  onChange={(e) => setReportType(e.target.value)}
                >
                  {reportTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Період часу */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.time_range')}</InputLabel>
                <Select
                  value={timeRange}
                  label={t('reports.time_range')}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  {timeRanges.map(range => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Кастомні дати */}
            {timeRange === 'custom' && (
              <>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label={t('reports.start_date')}
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label={t('reports.end_date')}
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            {/* Відділ */}
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>{t('reports.department')}</InputLabel>
                <Select
                  value={departmentFilter}
                  label={t('reports.department')}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('reports.all_departments')}</MenuItem>
                  {[...new Set(staff.map(s => s.department))].map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Кнопки експорту */}
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Tooltip title={t('reports.export_pdf')}>
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdf />}
                    onClick={() => generatePDFReport(reportData, reportType, showNotification, t)} // 👈 Додано параметри
                  >
                    PDF
                  </Button>
                </Tooltip>
                <Tooltip title={t('reports.export_excel')}>
                  <Button
                    variant="contained"
                    startIcon={<Download />}
                    onClick={() => generateExcelReport(reportData, reportType, showNotification, t)} // 👈 Додано параметри
                  >
                    Excel
                  </Button>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Статистика звіту */}
      {reportData.summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {Object.entries(reportData.summary).map(([key, value], index) => (
            <Grid item xs={12} sm={6} md={3} key={key}>
              <Card sx={{ 
                textAlign: 'center', 
                p: 3, 
                borderRadius: 3,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 2 
                }}>
                  {getSummaryIcon(key)}
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {typeof value === 'number' ? 
                    (key.includes('Rate') || key.includes('Percent') ? `${value}%` : value.toLocaleString()) 
                    : value}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {t(`reports.metrics.${key}`) || key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Графіки звіту */}
      {reportData.charts && reportData.charts.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {reportData.charts.map((chart, index) => (
            <Grid item xs={12} md={chart.fullWidth ? 12 : 6} key={index}>
              <Card sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  {getChartIcon(chart.type)}
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {chart.title}
                  </Typography>
                </Box>
                <ResponsiveContainer width="100%" height={300}>
                  {renderChart(chart)}
                </ResponsiveContainer>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Детальна таблиця */}
      {reportData.detailedData && reportData.detailedData.length > 0 && (
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Insights />
              {t('reports.detailed_analysis')}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {Object.keys(reportData.detailedData[0]).map(key => (
                      <TableCell key={key} sx={{ fontWeight: 'bold' }}>
                        {t(`reports.columns.${key}`) || key}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.detailedData.map((row, index) => (
                    <TableRow key={index} hover>
                      {Object.values(row).map((value, cellIndex) => (
                        <TableCell key={cellIndex}>
                          {typeof value === 'number' && cellIndex > 0 ? 
                            (value > 100 ? value.toLocaleString() : `${value}%`) 
                            : value}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Інформація про період */}
      <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
        <Typography variant="body2">
          {t('reports.report_period')}: {new Date(dateRange.start).toLocaleDateString()} - {new Date(dateRange.end).toLocaleDateString()}
        </Typography>
      </Alert>
    </Box>
  );
};

// Допоміжні функції
const getSummaryIcon = (metricKey) => {
  const icons = {
    totalStaff: <People sx={{ fontSize: 40, color: 'primary.main' }} />,
    attendanceRate: <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />,
    totalShifts: <Schedule sx={{ fontSize: 40, color: 'info.main' }} />,
    efficiency: <AutoGraph sx={{ fontSize: 40, color: 'warning.main' }} />,
    departmentCount: <Assessment sx={{ fontSize: 40, color: 'secondary.main' }} />
  };
  return icons[metricKey] || <Analytics sx={{ fontSize: 40, color: 'text.secondary' }} />;
};

const getChartIcon = (chartType) => {
  const icons = {
    bar: <BarChartIcon color="primary" />,
    line: <Timeline color="success" />,
    pie: <PieChartIcon color="info" />,
    area: <TrendingUp color="warning" />
  };
  return icons[chartType] || <Analytics color="primary" />;
};

const renderChart = (chart) => {
  switch (chart.type) {
    case 'bar':
      return (
        <BarChart data={chart.data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          <Bar dataKey="value" fill="#8884d8" radius={[4, 4, 0, 0]} />
        </BarChart>
      );
    case 'line':
      return (
        <LineChart data={chart.data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} dot={{ fill: '#8884d8' }} />
        </LineChart>
      );
    case 'pie':
      return (
        <PieChart> {/* 👈 Виправлено - тепер PieChart імпортовано */}
          <Pie
            data={chart.data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {chart.data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || `#${Math.floor(Math.random()*16777215).toString(16)}`} />
            ))}
          </Pie>
          <RechartsTooltip />
        </PieChart>
      );
    case 'area':
      return (
        <AreaChart data={chart.data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <RechartsTooltip />
          <Legend />
          <Area type="monotone" dataKey="value" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
        </AreaChart>
      );
    default:
      return null;
  }
};

const generateReportData = (reportType, staff, attendance, shifts) => {
  const baseData = calculateBaseMetrics(staff, attendance, shifts);
  
  switch (reportType) {
    case 'comprehensive':
      return generateComprehensiveReport(baseData);
    case 'attendance':
      return generateAttendanceReport(baseData);
    case 'performance':
      return generatePerformanceReport(baseData);
    case 'shifts':
      return generateShiftReport(baseData);
    case 'department':
      return generateDepartmentReport(baseData);
    default:
      return generateComprehensiveReport(baseData);
  }
};

const calculateBaseMetrics = (staff, attendance, shifts) => {
  const totalStaff = staff.length;
  const presentCount = attendance.filter(a => a.status === 'present').length;
  const attendanceRate = totalStaff > 0 ? (presentCount / attendance.length) * 100 : 0;
  
  return {
    totalStaff,
    totalAttendance: attendance.length,
    presentCount,
    attendanceRate: Math.round(attendanceRate),
    totalShifts: shifts.length,
    departments: [...new Set(staff.map(s => s.department))],
    departmentStats: calculateDepartmentStats(staff, attendance)
  };
};

const calculateDepartmentStats = (staff, attendance) => {
  return staff.reduce((acc, employee) => {
    const dept = employee.department || 'Unknown';
    if (!acc[dept]) {
      acc[dept] = { total: 0, present: 0 };
    }
    acc[dept].total++;
    
    const employeeAttendance = attendance.filter(a => a.employeeId === employee.id);
    acc[dept].present += employeeAttendance.filter(a => a.status === 'present').length;
    
    return acc;
  }, {});
};

const generateComprehensiveReport = (data) => {
  return {
    summary: {
      totalStaff: data.totalStaff,
      attendanceRate: data.attendanceRate,
      totalShifts: data.totalShifts,
      departmentCount: data.departments.length,
      efficiency: Math.round(data.attendanceRate * 0.8 + (data.totalShifts / data.totalStaff) * 20)
    },
    charts: [
      {
        type: 'bar',
        title: 'Department Performance',
        data: Object.entries(data.departmentStats).map(([dept, stats]) => ({
          name: dept,
          value: Math.round((stats.present / stats.total) * 100)
        }))
      },
      {
        type: 'line',
        title: 'Staff Distribution',
        data: Object.entries(data.departmentStats).map(([dept, stats]) => ({
          name: dept,
          value: stats.total
        }))
      }
    ],
    detailedData: Object.entries(data.departmentStats).map(([dept, stats]) => ({
      department: dept,
      employees: stats.total,
      attendanceRate: Math.round((stats.present / stats.total) * 100),
      performance: Math.round((stats.present / stats.total) * 100)
    }))
  };
};

const generateAttendanceReport = (data) => {
  return {
    summary: {
      totalStaff: data.totalStaff,
      attendanceRate: data.attendanceRate,
      presentCount: data.presentCount,
      totalRecords: data.totalAttendance
    },
    charts: [
      {
        type: 'pie',
        title: 'Attendance Distribution',
        data: [
          { name: 'Present', value: data.presentCount, color: '#4caf50' },
          { name: 'Absent', value: data.totalAttendance - data.presentCount, color: '#f44336' }
        ]
      }
    ],
    detailedData: Object.entries(data.departmentStats).map(([dept, stats]) => ({
      department: dept,
      attendanceRate: Math.round((stats.present / stats.total) * 100),
      present: stats.present,
      total: stats.total
    }))
  };
};

const generatePerformanceReport = (data) => {
  return {
    summary: {
      totalStaff: data.totalStaff,
      overallPerformance: data.attendanceRate,
      avgShiftsPerEmployee: (data.totalShifts / data.totalStaff).toFixed(1),
      departmentCount: data.departments.length
    },
    charts: [
      {
        type: 'bar',
        title: 'Department Performance',
        data: Object.entries(data.departmentStats).map(([dept, stats]) => ({
          name: dept,
          value: Math.round((stats.present / stats.total) * 100)
        }))
      }
    ],
    detailedData: Object.entries(data.departmentStats).map(([dept, stats]) => ({
      department: dept,
      performance: Math.round((stats.present / stats.total) * 100),
      employees: stats.total,
      attendanceScore: Math.round((stats.present / stats.total) * 100)
    }))
  };
};

const generateShiftReport = (data) => {
  return {
    summary: {
      totalShifts: data.totalShifts,
      shiftsPerEmployee: (data.totalShifts / data.totalStaff).toFixed(1),
      totalStaff: data.totalStaff,
      coverageRate: Math.round((data.totalShifts / (data.totalStaff * 30)) * 100)
    },
    charts: [
      {
        type: 'area',
        title: 'Shift Distribution',
        data: [
          { name: 'Week 1', value: Math.round(data.totalShifts * 0.3) },
          { name: 'Week 2', value: Math.round(data.totalShifts * 0.25) },
          { name: 'Week 3', value: Math.round(data.totalShifts * 0.25) },
          { name: 'Week 4', value: Math.round(data.totalShifts * 0.2) }
        ]
      }
    ],
    detailedData: Object.entries(data.departmentStats).map(([dept, stats]) => ({
      department: dept,
      estimatedShifts: Math.round(stats.total * 20),
      employees: stats.total,
      shiftRatio: (Math.round(stats.total * 20) / data.totalShifts * 100).toFixed(1)
    }))
  };
};

const generateDepartmentReport = (data) => {
  return {
    summary: {
      departmentCount: data.departments.length,
      totalStaff: data.totalStaff,
      avgStaffPerDept: Math.round(data.totalStaff / data.departments.length),
      largestDept: Object.entries(data.departmentStats).reduce((max, [dept, stats]) => 
        stats.total > max.total ? { dept, total: stats.total } : max, { dept: '', total: 0 }
      ).dept
    },
    charts: [
      {
        type: 'pie',
        title: 'Staff Distribution by Department',
        data: Object.entries(data.departmentStats).map(([dept, stats]) => ({
          name: dept,
          value: stats.total
        }))
      }
    ],
    detailedData: Object.entries(data.departmentStats).map(([dept, stats]) => ({
      department: dept,
      employees: stats.total,
      attendanceRate: Math.round((stats.present / stats.total) * 100),
      performance: Math.round((stats.present / stats.total) * 100)
    }))
  };
};

// 👈 Виправлені функції експорту - тепер приймають параметри
const generatePDFReport = (reportData, reportType, showNotification, t) => {
  showNotification(t('reports.pdf_generation_started'), 'info');
  console.log('Generating PDF report:', reportType, reportData);
  // Тут буде реальна генерація PDF
};

const generateExcelReport = (reportData, reportType, showNotification, t) => {
  showNotification(t('reports.excel_generation_started'), 'info');
  console.log('Generating Excel report:', reportType, reportData);
  // Тут буде реальна генерація Excel
};

export default AdvancedReportsPage;